// ecommerce-frontend/src/pages/FavoritesPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2, ArrowLeft, Eye, Home, ShoppingBag, Settings, User } from 'lucide-react';
import { authAPI, cartAPI } from '../services/api';

export default function FavoritesPage() {
  const navigate = useNavigate();
  const user = authAPI.getCurrentUser();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFavorites = () => {
    setLoading(true);
    try {
      const savedFavorites = localStorage.getItem('favorites');
      if (savedFavorites) {
        const parsed = JSON.parse(savedFavorites);
        setFavorites(Array.isArray(parsed) ? parsed : []);
      } else {
        setFavorites([]);
      }
    } catch (error) {
      console.error('Erreur lecture favoris:', error);
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = (productId) => {
    if (!Array.isArray(favorites)) return;
    const updatedFavorites = favorites.filter(fav => fav?.id !== productId);
    setFavorites(updatedFavorites);
    localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
  };

  const addToCart = async (product) => {
    if (!authAPI.isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      await cartAPI.add(product?.id, 1);
      alert(`${product?.name || 'Produit'} ajouté au panier !`);
    } catch (error) {
      console.error('Erreur ajout panier:', error);
      alert('Erreur lors de l\'ajout au panier');
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/300x300?text=Pas+d%27image';
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = 'http://localhost:8000';
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${baseUrl}${cleanPath}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl text-gray-300">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Formes décoratives */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"></div>
      
      <header className="bg-white/5 backdrop-blur-xl shadow-2xl sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-300 hover:text-orange-400 hover:bg-white/10 rounded-xl transition"
                title="Retour"
              >
                <ArrowLeft size={22} />
              </button>
              <div className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent cursor-pointer" onClick={() => navigate('/')}>
                MonMarket
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => navigate('/')} 
                className="p-2 text-gray-300 hover:text-orange-400 hover:bg-white/10 rounded-xl transition"
                title="Accueil"
              >
                <Home size={22} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 relative z-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Mes Favoris</h1>
          <p className="text-gray-400">
            {favorites.length > 0 
              ? `${favorites.length} produit${favorites.length > 1 ? 's' : ''} favori${favorites.length > 1 ? 's' : ''}`
              : 'Aucun favori pour le moment'
            }
          </p>
        </div>

        {favorites.length === 0 ? (
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl p-12 text-center border border-white/20">
            <Heart size={64} className="mx-auto mb-4 text-gray-600" />
            <h2 className="text-2xl font-bold text-white mb-2">
              Aucun favori
            </h2>
            <p className="text-gray-400 mb-6">
              Vous n'avez pas encore ajouté de produits à vos favoris
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-2xl hover:from-orange-600 hover:to-orange-700 transition shadow-xl"
            >
              Parcourir les produits
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {favorites.map((product) => (
              <div
                key={product.id}
                className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl overflow-hidden hover:bg-white/15 transition group border border-white/20"
              >
                <div className="relative">
                  <img
                    src={getImageUrl(product.image)}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/300x300?text=Image';
                    }}
                  />
                  <button
                    onClick={() => removeFavorite(product.id)}
                    className="absolute top-2 right-2 bg-white/90 backdrop-blur-md p-2 rounded-full shadow-lg hover:bg-white transition"
                  >
                    <Heart size={20} className="text-red-500 fill-red-500" />
                  </button>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => navigate(`/product/${product.id}`)}
                      className="bg-white text-gray-900 px-4 py-2 rounded-xl flex items-center space-x-2 transform translate-y-4 group-hover:translate-y-0 transition shadow-xl"
                    >
                      <Eye size={18} />
                      <span>Voir détails</span>
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="font-bold text-white mb-1 truncate">
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                    {product.description}
                  </p>

                  <div className="flex items-center justify-between mb-3">
                    <div className="text-2xl font-bold text-orange-400">
                      {product.price} FCFA
                    </div>
                    {product.stock > 0 ? (
                      <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded-full border border-green-400/30">
                        En stock
                      </span>
                    ) : (
                      <span className="text-xs bg-red-500/20 text-red-300 px-2 py-1 rounded-full border border-red-400/30">
                        Rupture
                      </span>
                    )}
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => addToCart(product)}
                      disabled={product.stock === 0}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2 rounded-xl hover:from-orange-600 hover:to-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg"
                    >
                      <ShoppingCart size={18} />
                      <span>Panier</span>
                    </button>
                    <button
                      onClick={() => removeFavorite(product.id)}
                      className="bg-red-500/20 text-red-400 p-2 rounded-xl hover:bg-red-500/30 transition border border-red-400/30"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}